import React from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import { SAP_COLORS, SAP_SPACING, SAP_SHADOWS } from '../../theme/sapDesignSystem';

// Types for dropdown options
export interface DropdownOption {
  label: string;
  value: string | number;
  code?: string;
  status?: number;
  disabled?: boolean;
  group?: string;
}

export interface SAPComboBoxProps {
  type: 'client' | 'job' | 'custom';
  value?: DropdownOption | null;
  onChange: (value: DropdownOption | null) => void;
  options?: DropdownOption[];
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  placeholder?: string;
  multiple?: boolean;
  freeSolo?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  required?: boolean;
  clearable?: boolean;
  groupBy?: (option: DropdownOption) => string;
  renderOption?: (props: any, option: DropdownOption) => React.ReactNode;
  filterOptions?: (options: DropdownOption[], params: any) => DropdownOption[];
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  noOptionsText?: string;
  sx?: any;
}

// Styled components following SAP design system
const StyledAutocomplete = styled(Autocomplete)(({ theme, error }: any) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: SAP_SPACING.component.borderRadius,
    transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
    border: `1px solid ${error ? SAP_COLORS.sapRed : SAP_COLORS.sapGray3}`,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
      borderColor: error ? SAP_COLORS.sapRed : SAP_COLORS.sapBlue,
    },
    
    '&.Mui-focused': {
      backgroundColor: SAP_COLORS.sapWhite,
      borderColor: error ? SAP_COLORS.sapRed : SAP_COLORS.sapBlue,
      boxShadow: `0 0 0 2px ${error ? SAP_COLORS.sapLightRed : SAP_COLORS.sapLightBlue}`,
    },
    
    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      borderColor: SAP_COLORS.sapGray3,
      color: SAP_COLORS.sapGray4,
    },
  },
  
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: 500,
    fontSize: '0.875rem',
    
    '&.Mui-focused': {
      color: error ? SAP_COLORS.sapRed : SAP_COLORS.sapBlue,
    },
    
    '&.Mui-error': {
      color: SAP_COLORS.sapRed,
    },
  },
  
  '& .MuiOutlinedInput-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: 400,
    fontSize: '0.875rem',
    padding: theme.spacing(1.5),
    
    '&::placeholder': {
      color: SAP_COLORS.sapGray4,
      opacity: 1,
    },
  },
  
  '& .MuiAutocomplete-popupIndicator': {
    color: SAP_COLORS.sapGray5,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
    },
  },
  
  '& .MuiAutocomplete-clearIndicator': {
    color: SAP_COLORS.sapGray5,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.sapLightRed,
      color: SAP_COLORS.sapRed,
    },
  },
}));

const StyledPopper = styled('div')({
  '& .MuiAutocomplete-paper': {
    backgroundColor: SAP_COLORS.sapWhite,
    border: `1px solid ${SAP_COLORS.sapGray3}`,
    borderRadius: SAP_SPACING.component.borderRadius,
    boxShadow: SAP_SHADOWS.lg,
    marginTop: SAP_SPACING.xs,
  },
  
  '& .MuiAutocomplete-listbox': {
    padding: SAP_SPACING.xs,
    maxHeight: '240px',
  },
  
  '& .MuiAutocomplete-option': {
    minHeight: '48px',
    padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
    borderRadius: SAP_SPACING.component.borderRadius,
    margin: `${SAP_SPACING.xs} 0`,
    fontSize: '0.875rem',
    color: SAP_COLORS.sapGray7,
    
    '&[aria-selected="true"]': {
      backgroundColor: SAP_COLORS.sapLightBlue,
      color: SAP_COLORS.sapDarkBlue,
      fontWeight: 500,
    },
    
    '&.Mui-focused': {
      backgroundColor: SAP_COLORS.hover,
    },
    
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
    },
  },
  
  '& .MuiAutocomplete-noOptions': {
    color: SAP_COLORS.sapGray5,
    fontSize: '0.875rem',
    textAlign: 'center',
    padding: SAP_SPACING.lg,
  },
  
  '& .MuiAutocomplete-groupLabel': {
    backgroundColor: SAP_COLORS.sapGray1,
    color: SAP_COLORS.sapGray6,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
    borderRadius: SAP_SPACING.component.borderRadius,
    margin: `${SAP_SPACING.xs} 0`,
  },
});

// Default render option with icons and status
const DefaultRenderOption = (type: string) => (props: any, option: DropdownOption) => {
  const getIcon = () => {
    switch (type) {
      case 'client':
        return <PersonIcon sx={{ fontSize: '1rem', color: SAP_COLORS.sapBlue, mr: 1 }} />;
      case 'job':
        return <WorkIcon sx={{ fontSize: '1rem', color: SAP_COLORS.sapGreen, mr: 1 }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status?: number) => {
    switch (status) {
      case 1:
        return SAP_COLORS.sapGreen;
      case 0:
        return SAP_COLORS.sapRed;
      default:
        return SAP_COLORS.sapGray5;
    }
  };

  return (
    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {getIcon()}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: SAP_COLORS.sapGray7,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {option.label}
        </Typography>
        {option.code && (
          <Typography
            variant="caption"
            sx={{
              color: SAP_COLORS.sapGray5,
              fontSize: '0.75rem',
            }}
          >
            Code: {option.code}
          </Typography>
        )}
      </Box>
      {typeof option.status === 'number' && (
        <Chip
          size="small"
          label={option.status === 1 ? 'Active' : 'Inactive'}
          sx={{
            height: '20px',
            fontSize: '0.625rem',
            fontWeight: 600,
            backgroundColor: option.status === 1 ? SAP_COLORS.sapLightGreen : SAP_COLORS.sapLightRed,
            color: getStatusColor(option.status),
            border: 'none',
          }}
        />
      )}
    </Box>
  );
};

export const SAPComboBox: React.FC<SAPComboBoxProps> = ({
  type,
  value,
  onChange,
  options = [],
  loading = false,
  disabled = false,
  error = false,
  helperText,
  label,
  placeholder,
  multiple = false,
  freeSolo = false,
  size = 'medium',
  fullWidth = true,
  required = false,
  clearable = true,
  groupBy,
  renderOption,
  filterOptions,
  onInputChange,
  noOptionsText = 'No options available',
  sx,
  ...rest
}) => {
  // Get appropriate placeholder and label based on type
  const getDefaults = () => {
    switch (type) {
      case 'client':
        return {
          defaultLabel: 'Client',
          defaultPlaceholder: 'Select a client...',
          defaultNoOptionsText: 'No clients found',
        };
      case 'job':
        return {
          defaultLabel: 'Job',
          defaultPlaceholder: 'Select a job...',
          defaultNoOptionsText: 'No jobs found',
        };
      default:
        return {
          defaultLabel: 'Select Option',
          defaultPlaceholder: 'Choose an option...',
          defaultNoOptionsText: 'No options available',
        };
    }
  };

  const defaults = getDefaults();
  const finalLabel = label || defaults.defaultLabel;
  const finalPlaceholder = placeholder || defaults.defaultPlaceholder;
  const finalNoOptionsText = noOptionsText || defaults.defaultNoOptionsText;

  // Default filter options that ignore disabled items
  const defaultFilterOptions = (options: DropdownOption[], params: any) => {
    const filtered = options.filter((option) => !option.disabled);
    return filtered.filter((option) =>
      option.label.toLowerCase().includes(params.inputValue.toLowerCase()) ||
      (option.code && option.code.toLowerCase().includes(params.inputValue.toLowerCase()))
    );
  };

  return (
    <Box sx={sx}>
      <StyledAutocomplete
        value={value}
        onChange={(event, newValue) => onChange(newValue as DropdownOption | null)}
        options={options}
        loading={loading}
        disabled={disabled}
        multiple={multiple}
        freeSolo={freeSolo}
        fullWidth={fullWidth}
        size={size}
        disableClearable={!clearable}
        groupBy={groupBy as any}
        filterOptions={filterOptions as any || defaultFilterOptions as any}
        onInputChange={onInputChange}
        noOptionsText={finalNoOptionsText}
        isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
        getOptionLabel={(option: any) => {
          if (typeof option === 'string') return option;
          return option.label || '';
        }}
        getOptionDisabled={(option: any) => option.disabled || false}
        renderOption={renderOption as any || DefaultRenderOption(type) as any}
        renderInput={(params) => (
          <TextField
            {...params}
            label={finalLabel}
            placeholder={finalPlaceholder}
            required={required}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1rem', color: SAP_COLORS.sapGray5 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        componentsProps={{
          popper: {
            sx: {
              '& .MuiAutocomplete-paper': {
                backgroundColor: SAP_COLORS.sapWhite,
                border: `1px solid ${SAP_COLORS.sapGray3}`,
                borderRadius: SAP_SPACING.component.borderRadius,
                boxShadow: SAP_SHADOWS.lg,
                marginTop: SAP_SPACING.xs,
              },
            },
          },
        }}
        {...rest}
      />
    </Box>
  );
};

export default SAPComboBox;